import React from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import AddTote from './AddTote';
import ApiError from '@/lib/shared-coms/ApiError';
import { Spinner } from 'equasion-lib';
import { useRootStore } from '@/lib/providers/RootProvider';
import { companyUsesTotes } from '@/lib/totes';
import { addToteReturnPath } from '@/lib/pickVerifyShip';

const CLIENT_ORDER = gql`
  query salesOrder($id: ID!, $vendorId: ID!, $vendorStatuses: [String!]) {
    salesOrder(id: $id, vendorId: $vendorId, vendorStatuses: $vendorStatuses) {
      id
      vendorSalesOrderNumber
      vendorStatus
    }
    # Re-read per screen-open, not from the root store: this route is directly addressable and can be
    # reached from a stale bundle after a mid-shift flip, in which case toteAssign will 403. The "id"
    # is required so this normalizes to Company:<id> (same field key as SalesOrderPicking's read).
    company(id: $vendorId) {
      id
      usesTotes
    }
  }
`;

const TOTE_ASSIGN = gql`
  mutation toteAssign($name: String!, $orderId: ID!, $companyId: ID!) {
    toteAssign(name: $name, orderId: $orderId, companyId: $companyId)
  }
`;

const AddToteData = (props) => {
  const vendorId = useRootStore((state) => state.selectedCompany.id);
  const [searchParams] = useSearchParams();
  const salesOrderId = searchParams.get('salesOrderId');
  // `returnTo=pick` — opened from the Pick → Verify → Ship Pick screen, so every way out of here
  // goes back there. Absent (every Option-1 link), the exits are exactly today's.
  const returnTo = searchParams.get('returnTo');
  const [toteAssign] = useMutation(TOTE_ASSIGN);

  const { data, error, loading, refetch } = useQuery(CLIENT_ORDER, {
    // MANDATORY. Without it Apollo's default cache-first serves this entire document — including
    // `usesTotes` — from whatever SalesOrderPicking normalized moments (or hours) earlier, and the
    // guard below silently reads the pre-flip value. Both sibling screens are network-only too.
    fetchPolicy: 'network-only',
    variables: { id: salesOrderId, vendorId, vendorStatuses: ['picking'] }
  });

  if (loading) return <Spinner />;
  if (error) return <ApiError error={error} />;

  const { salesOrder, company } = data || {};

  // The order left `picking` (shipped, split, cancelled) — nothing to bind a tote to. Parity with
  // SalesOrderPicking.data.tsx. `/sales-order-picking/` with an EMPTY id matches no route
  // (Routes.tsx has no catch-all) and would render a blank page with the history entry already
  // replaced, so never interpolate a missing id.
  if (!salesOrder) return <Navigate to="/sales-orders" replace />;

  // Route guard. `/add-tote` is directly addressable (Routes.tsx builds a static array — the route
  // cannot be conditionally registered), and the Add Tote CTA that normally leads here is hidden
  // when totes are off. `replace` so the back button cannot bounce the picker straight back into a
  // screen that would only 403.
  if (!companyUsesTotes(company)) {
    return <Navigate to={addToteReturnPath(salesOrder.id, returnTo)} replace />;
  }

  return (
    <AddTote
      toteAssign={toteAssign}
      salesOrder={salesOrder}
      companyId={vendorId}
      returnTo={returnTo}
      refetch={refetch}
      {...props}
    />
  );
};

export default AddToteData;
